import React, { useEffect, useState } from 'react';
import { Translate, useTranslate } from '~/i18n';

import { BusinessOnboardFilesForm } from '~/components/profile/onboard-files.section';
import { Button } from '~/components/common/button';
import { CheckGreenIcon } from '~/svgs/marketplace';
import { CloseIconGray, SaudiMapIcon, InternationalMapIcon, RightArrowGreen } from '~/svgs';
import Modal from '~/components/common/modal';
import { Section } from '~/components/common/section';
import cn from 'classnames';
import { useAuth } from '~/auth';
import { useRouter } from 'next/router';

type OnboardingModalType = {
   open: boolean;
   setOpen: (value: boolean) => void;
   setOpenSkipOnboarding: (value: boolean) => void;
   refetchUser?: () => void;
};

const OnboardingModal = ({
   open,
   setOpen,
   setOpenSkipOnboarding,
   refetchUser,
}: OnboardingModalType) => {
   const { user } = useAuth();
   // const router = useRouter();
   const [onboardingStep, setOnboardingStep] = useState<
      'welcome' | 'onboard_list' | 'upload_files' | 'in_review' | undefined
   >(undefined);
   const [isDomesticUser, setIsDomesticUser] = useState<boolean>(false);
   const [isNusukUser, setIsNusukUser] = useState<boolean>(false);

   useEffect(() => {
      if (user && !user?.skipOnBoarding && !onboardingStep) {
         setOnboardingStep('welcome');
      }
   }, [onboardingStep, user]);

   const fwdBtnHandler = () => {
      setOnboardingStep('in_review');
   };

   return (
      <Modal
         size={onboardingStep === 'in_review' ? 'sm' : onboardingStep === 'welcome' ? 1100 : 'lg'}
         open={open}>
         <Section>
            {onboardingStep === 'in_review' ? (
               <InReviewState
                  setOpen={setOpen}
                  setOpenSkipOnboarding={setOpenSkipOnboarding}
                  refetchUser={refetchUser}
               />
            ) : onboardingStep === 'welcome' ? (
               <WelcomeScreen
                  setOpen={setOpen}
                  setOnboardingStep={setOnboardingStep}
                  setOpenSkipOnboarding={setOpenSkipOnboarding}
                  setIsDomesticUser={setIsDomesticUser}
                  setIsNusukUser={setIsNusukUser}
               />
            ) : onboardingStep === 'onboard_list' ? (
               <OnboardList
                  setOpen={setOpen}
                  setOnboardingStep={setOnboardingStep}
                  setOpenSkipOnboarding={setOpenSkipOnboarding}
               />
            ) : (
               <BusinessOnboardFilesForm
                  sectionClassName="!w-full !px-3 mx-auto !pt-8 sm:!px-6"
                  formClassName="!w-full rounded-lg"
                  fwdBtnHandler={fwdBtnHandler}
                  cancelBtnTitle={<Translate id="profile:upload later" />}
                  fwdBtnTitle={<Translate id="common:submit" />}
                  footerClassName="flex justify-end bg-white"
                  cancelBtnHandler={() => {
                     setOpen(false);
                     setOpenSkipOnboarding(false);
                  }}
                  isDomesticUser={isDomesticUser}
                  isNusukUser={isNusukUser}
               />
            )}
         </Section>
      </Modal>
   );
};

const WelcomeScreen = ({
   setOnboardingStep,
   setIsDomesticUser,
   setIsNusukUser,
}: {
   setOpen: (value: boolean) => void;
   setOnboardingStep: (value: 'welcome' | 'onboard_list' | undefined) => void;
   setOpenSkipOnboarding: (value: boolean) => void;
   setIsDomesticUser: (value: boolean) => void;
   setIsNusukUser: (value: boolean) => void;
}) => {
   const { user, updateUser } = useAuth();
   const { lang } = useTranslate();
   const userTypeList = [
      {
         icon: <img src="/images/dashboard/kaaba-icon.png" width={28} alt="kaaba" />,
         title: <Translate id="profile:umrah packages" />,
         description: (
            <Translate
               parseHTML
               id="profile:offer umra info"
               values={{
                  url: `https://book.nusuk.sa/sa-${lang}`,
               }}
            />
         ),
         type: 'is_umrah',
      },
      {
         icon: <SaudiMapIcon width={28} height={28} />,
         title: <Translate id="profile:tourism packages" />,
         description: (
            <Translate
               parseHTML
               id="profile:domestic info"
               values={{
                  url: `https://book.visitsaudi.com/sa-${lang}`,
               }}
            />
         ),
         type: 'domestic',
      },
      {
         icon: <InternationalMapIcon width={28} height={28} />,
         title: <Translate id="profile:international resellers" />,
         description: <Translate id="profile:international info" />,
         type: 'international',
      },
   ];
   return (
      <div>
         <div className="flex items-center justify-between px-10 py-6">
            <span className="block text-xl font-semibold">
               <Translate id="profile:what describes your goal on the platform?" />
            </span>
         </div>
         <hr />
         <div className="flex flex-col pb-3">
            <div className="w-full py-8 px-10 flex flex-col lg:flex-row items-center gap-5">
               {userTypeList.map((item, index) => (
                  <div
                     key={index}
                     className="relative w-full md:w-1/2 py-4 flex flex-col rounded-lg gap-2 cursor-pointer border border-primary hover:shadow-lg"
                     onClick={() => {
                        setIsDomesticUser(item?.type === 'domestic');
                        setIsNusukUser(item?.type === 'is_umrah');
                        updateUser({
                           ...user,
                           international: item?.type === 'international',
                           is_umrah: item?.type === 'is_umrah',
                        });
                        setOnboardingStep('onboard_list');
                     }}>
                     <div className="px-4 flex justify-between">
                        <span>{item.icon}</span>
                        {item?.type === 'is_umrah' ? (
                           <img
                              className="absolute top-2.5 ltr:right-3 rtl:left-3"
                              src="/images/brands/powered-by-nusuk.svg"
                              alt="kaaba"
                              width={70}
                           />
                        ) : null}
                     </div>
                     <div className="text-lg px-4">{item.title}</div>
                     <div className="text-sm px-4 min-h-[60px] text-grey-200">
                        {item.description}
                     </div>
                     <div className="border-t pt-2 flex justify-between items-center px-4">
                        <div className="text-sm text-primary font-semibold">
                           <Translate id="profile:select" />
                        </div>
                        <RightArrowGreen
                           width={20}
                           height={20}
                           className="[&>*]:fill-primary rtl:-rotate-180"
                        />
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>
   );
};

const OnboardList = ({
   setOpen,
   setOnboardingStep,
   setOpenSkipOnboarding,
}: {
   setOpen: (value: boolean) => void;
   setOnboardingStep: (value: 'welcome' | 'upload_files' | undefined) => void;
   setOpenSkipOnboarding: (value: boolean) => void;
}) => {
   const { user } = useAuth();
   const initSteps = [
      {
         title: <Translate id="profile:create your account" />,
         description: <Translate id="profile:complete your account info" />,
         isComplete: true,
         btnAction: null,
      },
      {
         title: <Translate id="profile:upload legal documents" />,
         description: <Translate id="profile:upload legal documents info" />,
         isComplete: user?.uploaded_legal_docs,
         btnAction: () => setOnboardingStep('upload_files'),
         status: <Translate id="profile:upload" />,
      },
      {
         title: <Translate id="profile:account activation" />,
         description: <Translate id="profile:account activation info" />,
         isComplete: !!user?.profile_strength?.verified_business_profile,
         btnAction: null,
         status: <Translate id="common:pending" />,
      },
   ];
   return (
      <div>
         <div className="flex items-center justify-between p-8">
            <span className="block text-2xl font-semibold">
               <Translate id="profile:complete your account" />
            </span>
            {/* <div className="flex items-center">
               <button
                  onClick={() => {
                     setOpen(false);
                     setOpenSkipOnboarding(false);
                  }}
                  type="button"
                  className="focus:outline-none focus:ring-0">
                  <CloseIconGray />
               </button>
            </div> */}
         </div>
         <hr />
         <div className="flex flex-col">
            {initSteps?.map((step, idx) => (
               <>
                  <div className="flex justify-between items-center px-8 pt-2 pb-4">
                     <div className="flex items-center gap-4">
                        <span className="-mt-4">
                           {!step?.isComplete ? (
                              <span className="py-0.5 px-2 rounded-full bg-gray-300 text-grey-200 text-sm font-semibold">
                                 {idx + 1}
                              </span>
                           ) : (
                              <CheckGreenIcon width={24} height={24} />
                           )}
                        </span>
                        <div className="flex flex-col">
                           <div
                              className={cn(
                                 'my-2 text-lg font-semibold',
                                 step?.isComplete && 'text-primary'
                              )}>
                              {step?.title}
                           </div>
                           <div className="text-grey-300">{step?.description}</div>
                        </div>
                     </div>
                     <div
                        className={cn(
                           'text-md font-semibold uppercase pt-2',
                           step?.isComplete && 'text-primary'
                        )}>
                        {
                           step?.isComplete ? (
                              <Translate id="common:completed" />
                           ) : step?.btnAction ? (
                              <Button
                                 type="button"
                                 theme="outline-primary"
                                 disabled={!user?.profile_strength?.has_business_profile}
                                 className="!font-semibold !px-8"
                                 onClick={step?.btnAction}>
                                 {step?.status}
                              </Button>
                           ) : null
                           // <div className="flex items-center gap-2 text-light-orange">
                           //    <SpinnerIcon width={28} height={28} />
                           //    {!user?.profile_strength?.has_business_profile ? (
                           //       <Translate id="dashboard:under review" />
                           //    ) : (
                           //       step?.status
                           //    )}
                           // </div>
                        }
                     </div>
                  </div>
                  <hr />
               </>
            ))}
         </div>

         <div className="flex justify-end p-5">
            <Button
               type="button"
               theme="primary"
               className="!font-normal !px-16"
               onClick={() => {
                  setOpen(false);
                  setOpenSkipOnboarding(false);
               }}>
               <Translate id="common:done" />
            </Button>
         </div>
      </div>
   );
};

const InReviewState = ({
   setOpen,
   setOpenSkipOnboarding,
   refetchUser,
}: {
   setOpen: (value: boolean) => void;
   setOpenSkipOnboarding: (value: boolean) => void;
   refetchUser?: () => void;
}) => {
   const router = useRouter();
   const { lang } = useTranslate();
   return (
      <div className="px-5 pt-14 pb-10">
         <button
            onClick={() => {
               refetchUser?.();
               setOpen(false);
               setOpenSkipOnboarding(false);
            }}
            type="button"
            className="absolute z-10 rounded-full focus:outline-none focus:ring-0 top-8 ltr:right-5 rtl:left-5 ">
            <CloseIconGray />
         </button>
         <div className="flex flex-col items-center text-center space-y-4">
            <CheckGreenIcon width={35} height={35} />
            <div className="my-2 text-2xl font-bold capitalize px-20">
               <Translate id="profile:documents uploaded" />
            </div>
            <div className="text-sm text-grey-300 px-10 pt-3 pb-5">
               <Translate id="profile:documents uploaded description" />
            </div>
         </div>
         <div className="flex justify-center p-4">
            <Button
               type="button"
               theme="primary"
               className="!w-1/2 !font-normal ml-2"
               onClick={() => {
                  refetchUser?.();
                  router.push(`/${lang}/marketplace`);
               }}>
               <Translate id="marketplace:visit marketplace" />
            </Button>
         </div>
      </div>
   );
};

export default OnboardingModal;
